import { Alert, Box, LoadingOverlay, Stack, Text, Title } from '@mantine/core';
import classes from './FormLayout.module.css';
import type { FormEventHandler, ReactNode } from 'react';

type FormLayoutProps = {
    title?: string;
    subtitle?: string;
    children: ReactNode;
    successMessage?: string | null;
    errorMessage?: string | null;
    isSubmitting?: boolean;
    /** Optional form submission handler. If provided, the root Box will render as a form element. */
    onSubmit?: FormEventHandler<HTMLFormElement>;
};

// snippet:start:form-layout-component
export const FormLayout = ({
    title,
    subtitle,
    children,
    successMessage,
    errorMessage,
    isSubmitting = false,
    onSubmit,
}: FormLayoutProps) => {
    const handleFormSubmit = onSubmit
        ? (e: React.FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              onSubmit(e);
          }
        : undefined;

    const rootProps = handleFormSubmit
        ? {
              component: 'form' as const,
              w: '100%' as const,
              onSubmit: handleFormSubmit,
              className: classes.root,
              pb: { base: 80, sm: 0 },
          }
        : { className: classes.root };

    return (
        <Box {...rootProps}>
            <LoadingOverlay visible={isSubmitting} overlayProps={{ blur: 2 }} />

            <Box size="sm" className={classes.container}>
                <Stack gap="sm">
                    {title && (
                        <Title order={1} className={classes.title}>
                            {title}
                        </Title>
                    )}
                    {subtitle && (
                        <Text size="sm" c="dimmed">
                            {subtitle}
                        </Text>
                    )}

                    {successMessage && (
                        <Alert color="green" title="Success">
                            {successMessage}
                        </Alert>
                    )}

                    {errorMessage && (
                        <Alert color="red" title="Error">
                            {errorMessage}
                        </Alert>
                    )}

                    {children}
                </Stack>
            </Box>
        </Box>
    );
};
// snippet:end:form-layout-component
